'use client';

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Alert,
  Space,
  Button,
  Tabs,
  Progress,
  Table,
  Input,
} from 'antd';
import {
  FileTextOutlined,
  ShoppingOutlined,
  InboxOutlined,
  DatabaseOutlined,
  BranchesOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  PlusOutlined,
  BarChartOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/components/AuthContext';
import { PASTEL_THEME, PastelStatusBadge } from '@/components/PastelBadge';
import {
  DashboardKPIs,
  OutstandingPOItemReport,
  TraceabilityLedgerRecord,
  SupplierPerformanceReport,
} from '@/services/reportService';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

export default function HomePage() {
  const router = useRouter();
  const { user, activeRole } = useAuth();

  const [loading, setLoading] = useState<boolean>(true);
  const [kpis, setKpis] = useState<DashboardKPIs | null>(null);
  const [outstandingPOs, setOutstandingPOs] = useState<OutstandingPOItemReport[]>([]);
  const [traceability, setTraceability] = useState<TraceabilityLedgerRecord[]>([]);
  const [supplierQuality, setSupplierQuality] = useState<SupplierPerformanceReport[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeReportTab, setActiveReportTab] = useState<string>('outstanding');

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/reports');
      const json = await res.json();
      if (json.success && json.data) {
        setKpis(json.data.kpis);
        setOutstandingPOs(json.data.outstandingPOs);
        setTraceability(json.data.traceability);
        setSupplierQuality(json.data.supplierQuality);
      }
    } catch {
      // ignore fetch errors
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        const res = await fetch('/api/reports');
        const json = await res.json();
        if (!ignore && json.success && json.data) {
          setKpis(json.data.kpis);
          setOutstandingPOs(json.data.outstandingPOs);
          setTraceability(json.data.traceability);
          setSupplierQuality(json.data.supplierQuality);
        }
      } catch {
        // ignore fetch errors
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, []);

  // Filtered Outstanding POs for dashboard preview
  const filteredOutstanding = useMemo(() => {
    return outstandingPOs.filter(
      (item) =>
        item.poNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.supplierName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.itemName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [outstandingPOs, searchQuery]);

  // Filtered Traceability
  const filteredTraceability = useMemo(() => {
    return traceability.filter(
      (t) =>
        t.prNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.poNumber && t.poNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (t.grNumber && t.grNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
        t.itemName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [traceability, searchQuery]);

  // Filtered Quality
  const filteredQuality = useMemo(() => {
    return supplierQuality.filter((s) =>
      s.supplierName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [supplierQuality, searchQuery]);

  // Outstanding PO columns preview
  const outstandingColumns: ColumnsType<OutstandingPOItemReport> = [
    {
      title: 'PO Number & Date',
      key: 'poNumber',
      width: 170,
      render: (_, rec) => (
        <div>
          <a
            onClick={() => router.push(`/purchase-orders/${rec.poId}`)}
            style={{ fontWeight: 700, color: '#4F46E5' }}
          >
            {rec.poNumber}
          </a>
          <div style={{ fontSize: 11, color: '#64748B' }}>
            {new Date(rec.poDate).toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </div>
          {rec.originPRNumber && (
            <Tag color="cyan" style={{ fontSize: 10, marginTop: 2, padding: '0 4px', lineHeight: '16px' }}>
              via {rec.originPRNumber}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      key: 'supplierName',
      render: (name: string) => (
        <span style={{ fontWeight: 600, color: '#1E293B' }}>{name}</span>
      ),
    },
    {
      title: 'Item Description',
      key: 'item',
      render: (_, rec) => (
        <div>
          <div style={{ fontWeight: 600, color: '#0F172A' }}>{rec.itemName}</div>
          <Tag color="purple" style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px' }}>
            {rec.itemCode}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Quantities',
      key: 'quantities',
      width: 170,
      render: (_, rec) => (
        <div style={{ fontSize: 12 }}>
          <div>Ordered: <strong>{rec.orderedQty} {rec.uomCode}</strong></div>
          <div style={{ color: '#16A34A' }}>Received: {rec.receivedQty} {rec.uomCode}</div>
          <div style={{ color: rec.outstandingQty > 0 ? '#E11D48' : '#64748B', fontWeight: 700 }}>
            Pending: {rec.outstandingQty} {rec.uomCode}
          </div>
        </div>
      ),
    },
    {
      title: 'Fulfillment',
      key: 'fulfillment',
      width: 150,
      render: (_, rec) => (
        <div style={{ width: 120 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
            <PastelStatusBadge status={rec.poStatus} size="small" />
            <Text style={{ fontSize: 11, fontWeight: 700 }}>{rec.fulfillmentPercent}%</Text>
          </div>
          <Progress
            percent={rec.fulfillmentPercent}
            size="small"
            status={rec.fulfillmentPercent === 100 ? 'success' : 'active'}
            strokeColor={rec.fulfillmentPercent === 100 ? '#10B981' : '#6366F1'}
          />
        </div>
      ),
    },
    {
      title: 'Outstanding Value',
      key: 'value',
      align: 'right',
      width: 150,
      render: (_, rec) => (
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 800, color: rec.outstandingValue > 0 ? '#B91C1C' : '#15803D' }}>
            Rp {rec.outstandingValue.toLocaleString('id-ID')}
          </div>
          <div style={{ fontSize: 11, color: '#64748B' }}>
            @ Rp {rec.unitPrice.toLocaleString('id-ID')}
          </div>
        </div>
      ),
    },
  ];

  // Traceability columns preview
  const traceabilityColumns: ColumnsType<TraceabilityLedgerRecord> = [
    {
      title: 'Stage',
      dataIndex: 'pipelineStage',
      key: 'pipelineStage',
      width: 140,
      render: (stage: string) => {
        if (stage === 'COSTED_INVENTORY') {
          return <Tag color="green"><DatabaseOutlined /> Stock Costed</Tag>;
        }
        if (stage === 'GR_STAGE') {
          return <Tag color="blue"><InboxOutlined /> GR Received</Tag>;
        }
        if (stage === 'PO_STAGE') {
          return <Tag color="orange"><ShoppingOutlined /> PO Committed</Tag>;
        }
        return <Tag color="default"><FileTextOutlined /> PR Requisition</Tag>;
      },
    },
    {
      title: 'Item Specification',
      key: 'item',
      render: (_, rec) => (
        <div>
          <div style={{ fontWeight: 700, color: '#0F172A' }}>{rec.itemName}</div>
          <Tag color="purple" style={{ fontSize: 10 }}>{rec.itemCode}</Tag>
          <span style={{ fontSize: 11, color: '#475569' }}>
            Req: <strong>{rec.requestedQty} {rec.uomCode}</strong>
          </span>
        </div>
      ),
    },
    {
      title: 'Requisition (PR)',
      key: 'pr',
      render: (_, rec) => (
        <div>
          <a onClick={() => router.push(`/purchase-requests/${rec.prId}`)} style={{ fontWeight: 600 }}>
            {rec.prNumber}
          </a>
          <div style={{ fontSize: 11, color: '#64748B' }}>
            {rec.department} ({rec.requesterName})
          </div>
        </div>
      ),
    },
    {
      title: 'Order (PO)',
      key: 'po',
      render: (_, rec) => {
        if (!rec.poNumber) return <Text type="secondary" italic style={{ fontSize: 12 }}>Unallocated</Text>;
        return (
          <div>
            <a onClick={() => router.push(`/purchase-orders/${rec.poId}`)} style={{ fontWeight: 600, color: '#4F46E5' }}>
              {rec.poNumber}
            </a>
            <div style={{ fontSize: 11, color: '#1E293B', fontWeight: 600 }}>{rec.supplierName}</div>
            <div style={{ fontSize: 11, color: '#64748B' }}>
              Ordered: {rec.orderedQty} {rec.uomCode}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Goods Receipt & Cost Layer',
      key: 'gr',
      render: (_, rec) => {
        if (!rec.grNumber) return <Text type="secondary" italic style={{ fontSize: 12 }}>Pending Receipt</Text>;
        return (
          <div>
            <div>
              <a onClick={() => router.push(`/goods-receipts/${rec.grId}`)} style={{ fontWeight: 600, color: '#0284C7' }}>
                {rec.grNumber}
              </a>
              <span style={{ fontSize: 11, color: '#64748B', marginLeft: 6 }}>SJ: {rec.vendorDeliveryNote}</span>
            </div>
            <div style={{ fontSize: 11 }}>
              <span style={{ color: '#16A34A', fontWeight: 700 }}>Good: {rec.goodReceivedQty} {rec.uomCode}</span>
              {(rec.rejectedQty ?? 0) > 0 && (
                <span style={{ color: '#E11D48', fontWeight: 700, marginLeft: 6 }}>Rej: {rec.rejectedQty}</span>
              )}
            </div>
            {rec.costLayerId && (
              <Tag color="cyan" style={{ fontSize: 10, marginTop: 2 }}>
                Cost Layer: {rec.costLayerId} (@ Rp {rec.unitCost?.toLocaleString('id-ID')})
              </Tag>
            )}
          </div>
        );
      },
    },
  ];

  // Supplier performance columns
  const qualityColumns: ColumnsType<SupplierPerformanceReport> = [
    {
      title: 'Supplier Name',
      key: 'name',
      render: (_, s) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0F172A' }}>{s.supplierName}</div>
          <Tag color="blue" style={{ fontSize: 10 }}>{s.supplierCode}</Tag>
          <span style={{ fontSize: 11, color: '#64748B' }}>{s.phone}</span>
        </div>
      ),
    },
    {
      title: 'Orders & Receipts',
      key: 'orders',
      render: (_, s) => (
        <div style={{ fontSize: 12 }}>
          <div>Orders: <strong>{s.totalPOs} PO(s)</strong> (Rp {s.totalSpend.toLocaleString('id-ID')})</div>
          <div>Shipments: <strong>{s.totalGRs} receipt(s)</strong></div>
        </div>
      ),
    },
    {
      title: 'Inspection Breakdown',
      key: 'inspection',
      render: (_, s) => (
        <div style={{ fontSize: 12 }}>
          <div>Good / Accepted: <strong style={{ color: '#16A34A' }}>{s.totalGoodQty.toLocaleString('id-ID')}</strong></div>
          <div>Damaged / Defect: <strong style={{ color: s.totalRejectedQty > 0 ? '#E11D48' : '#64748B' }}>{s.totalRejectedQty.toLocaleString('id-ID')}</strong></div>
        </div>
      ),
    },
    {
      title: 'Quality Acceptance Rate',
      key: 'acceptance',
      width: 220,
      render: (_, s) => (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Acceptance</span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: s.acceptanceRate >= 98 ? '#15803D' : '#D97706',
              }}
            >
              {s.acceptanceRate}%
            </span>
          </div>
          <Progress
            percent={s.acceptanceRate}
            size="small"
            status={s.acceptanceRate < 95 ? 'exception' : 'success'}
            strokeColor={s.acceptanceRate >= 98 ? '#10B981' : '#F59E0B'}
          />
          <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
            {s.rejectionIncidentsCount === 0 ? (
              <span style={{ color: '#16A34A' }}>&check; Zero rejection incidents</span>
            ) : (
              <span style={{ color: '#E11D48' }}>&bull; {s.rejectionIncidentsCount} rejection incident(s)</span>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', paddingBottom: 40 }}>
      {/* Top Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0, flex: '1 1 240px' }}>
          <Title level={2} style={{ margin: 0, color: '#0F172A', fontWeight: 800 }}>
            Executive Purchase Dashboard
          </Title>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
            PT XYZ ERP Enterprise Procurement & Goods Receipt Management
          </Text>
        </div>
        <Space size="small" wrap>
          <Button icon={<ReloadOutlined />} onClick={fetchDashboardData} style={{ borderRadius: 10 }}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<BarChartOutlined />}
            onClick={() => router.push('/reports')}
            style={{
              borderRadius: 10,
              background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
              fontWeight: 600,
            }}
          >
            Laporan Lengkap
          </Button>
        </Space>
      </div>

      {/* Role Simulator Alert */}
      <Alert
        title={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
              <Space size="small" wrap>
                <Text strong style={{ fontSize: 13 }}>Role Simulasi Aktif:</Text>
                <Tag color="purple" style={{ fontWeight: 700, borderRadius: 6, margin: 0 }}>{activeRole}</Tag>
              </Space>
              <Button
                size="small"
                type="link"
                onClick={() => router.push('/admin/users')}
                style={{ padding: 0, fontWeight: 600, fontSize: 12 }}
              >
                Ganti Role di Header &rarr;
              </Button>
            </div>
            <div style={{ fontSize: 12, color: '#64748B', lineHeight: 1.4 }}>
              Masuk sebagai <strong>{user.name}</strong> ({user.email}). UI kontrol dan hak akses otomatis menyesuaikan persona ini.
            </div>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 20, borderRadius: 12, border: '1px solid #BAE6FD', background: '#F0F9FF', padding: '10px 14px' }}
      />

      {/* 5 Key Executive KPI Pastel Cards */}
      <Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
        {/* 1. Total Spend */}
        <Col xs={12} sm={12} md={8} lg={4.8} xl={4.8}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #EEF2FF 100%)',
              padding: '14px 14px',
              borderRadius: 14,
              border: '1px solid #E0E7FF',
              boxShadow: PASTEL_THEME.boxShadowCard,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#4F46E5', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Total Spend
                </span>
                <DollarOutlined style={{ color: '#6366F1', fontSize: 16 }} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#1E1B4B', marginTop: 4, wordBreak: 'break-word' }}>
                Rp {(kpis?.totalSpend ?? 0).toLocaleString('id-ID')}
              </div>
            </div>
            <div style={{ fontSize: 10, color: '#6B7280', marginTop: 6 }}>
              PO commitments
            </div>
          </div>
        </Col>

        {/* 2. Open POs */}
        <Col xs={12} sm={12} md={8} lg={4.8} xl={4.8}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #E0F2FE 100%)',
              padding: '14px 14px',
              borderRadius: 14,
              border: '1px solid #BAE6FD',
              boxShadow: PASTEL_THEME.boxShadowCard,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#0284C7', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Open POs
                </span>
                <ShoppingOutlined style={{ color: '#0284C7', fontSize: 16 }} />
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0C4A6E', marginTop: 4 }}>
                {kpis?.openPOsCount ?? 0}
              </div>
            </div>
            <div style={{ fontSize: 10, color: '#6B7280', marginTop: 6 }}>
              Active issued POs
            </div>
          </div>
        </Col>

        {/* 3. Outstanding Commitments */}
        <Col xs={12} sm={12} md={8} lg={4.8} xl={4.8}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF1F2 100%)',
              padding: '14px 14px',
              borderRadius: 14,
              border: '1px solid #FECDD3',
              boxShadow: PASTEL_THEME.boxShadowCard,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#BE123C', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Commitments
                </span>
                <ClockCircleOutlined style={{ color: '#E11D48', fontSize: 16 }} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#881337', marginTop: 4, wordBreak: 'break-word' }}>
                Rp {(kpis?.outstandingCommitmentsValue ?? 0).toLocaleString('id-ID')}
              </div>
            </div>
            <div style={{ fontSize: 10, color: '#6B7280', marginTop: 6 }}>
              Pending goods
            </div>
          </div>
        </Col>

        {/* 4. Pending PRs */}
        <Col xs={12} sm={12} md={8} lg={4.8} xl={4.8}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FEF3C7 100%)',
              padding: '14px 14px',
              borderRadius: 14,
              border: '1px solid #FDE68A',
              boxShadow: PASTEL_THEME.boxShadowCard,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#B45309', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Pending PRs
                </span>
                <FileTextOutlined style={{ color: '#D97706', fontSize: 16 }} />
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#78350F', marginTop: 4 }}>
                {kpis?.pendingPRsCount ?? 0}
              </div>
            </div>
            <div style={{ fontSize: 10, color: '#6B7280', marginTop: 6 }}>
              {kpis?.pendingPRItemsCount ?? 0} items await review
            </div>
          </div>
        </Col>

        {/* 5. Inventory Valuation */}
        <Col xs={24} sm={12} md={8} lg={4.8} xl={4.8}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #DCFCE7 100%)',
              padding: '14px 14px',
              borderRadius: 14,
              border: '1px solid #BBF7D0',
              boxShadow: PASTEL_THEME.boxShadowCard,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#15803D', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Inventory Stock
                </span>
                <DatabaseOutlined style={{ color: '#16A34A', fontSize: 16 }} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#14532D', marginTop: 4, wordBreak: 'break-word' }}>
                Rp {(kpis?.totalInventoryValuation ?? 0).toLocaleString('id-ID')}
              </div>
            </div>
            <div style={{ fontSize: 10, color: '#6B7280', marginTop: 6 }}>
              {kpis?.activeCostLayersCount ?? 0} active cost layers
            </div>
          </div>
        </Col>
      </Row>

      {/* Quick Actions Bar */}
      <Card
        variant="borderless"
        style={{
          borderRadius: 14,
          marginBottom: 24,
          background: 'linear-gradient(135deg, #FAF5FF 0%, #FFFFFF 100%)',
          border: '1px solid #E9D5FF',
          boxShadow: PASTEL_THEME.boxShadowCard,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#6B21A8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Quick Action Center
            </span>
            <div style={{ fontSize: 12, color: '#64748B' }}>
              Execute critical procurement workflows per authorized role
            </div>
          </div>
          <Space wrap size="middle">
            <Button
              icon={<PlusOutlined />}
              onClick={() => router.push('/purchase-requests/new')}
              style={{
                borderRadius: 10,
                background: '#F0FDF4',
                borderColor: '#BBF7D0',
                color: '#15803D',
                fontWeight: 600,
              }}
            >
              New Purchase Request
            </Button>
            <Button
              icon={<BranchesOutlined />}
              onClick={() => router.push('/purchase-orders/consolidate')}
              style={{
                borderRadius: 10,
                background: '#EFF6FF',
                borderColor: '#BFDBFE',
                color: '#1D4ED8',
                fontWeight: 600,
              }}
            >
              Consolidate PO
            </Button>
            <Button
              icon={<InboxOutlined />}
              onClick={() => router.push('/goods-receipts/receive')}
              style={{
                borderRadius: 10,
                background: '#FAF5FF',
                borderColor: '#E9D5FF',
                color: '#7E22CE',
                fontWeight: 600,
              }}
            >
              Receive Goods
            </Button>
            <Button
              icon={<DatabaseOutlined />}
              onClick={() => router.push('/inventory')}
              style={{
                borderRadius: 10,
                background: '#F8FAFC',
                borderColor: '#E2E8F0',
                color: '#334155',
                fontWeight: 600,
              }}
            >
              Inventory View
            </Button>
          </Space>
        </div>
      </Card>

      {/* Procurement Lifecycle Stepper Banner */}
      <Card
        variant="borderless"
        style={{
          borderRadius: 14,
          marginBottom: 24,
          background: '#FFFFFF',
          boxShadow: PASTEL_THEME.boxShadowCard,
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ fontSize: 14, color: '#1E293B' }}>
            Enterprise Procurement Lifecycle & Business Directives
          </Text>
        </div>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} lg={6}>
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: '#F8FAFC',
                border: '1px solid #E2E8F0',
                height: '100%',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#4F46E5', fontWeight: 700, fontSize: 13 }}>
                <FileTextOutlined /> 1. Line-Item Approval
              </div>
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
                Owner approves or rejects items individually. PR aggregates status dynamically.
              </div>
            </div>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: '#F8FAFC',
                border: '1px solid #E2E8F0',
                height: '100%',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0284C7', fontWeight: 700, fontSize: 13 }}>
                <BranchesOutlined /> 2. Multi-PR to 1 PO
              </div>
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
                Purchaser consolidates approved PR items for the same supplier. Bank info snapshotted.
              </div>
            </div>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: '#F8FAFC',
                border: '1px solid #E2E8F0',
                height: '100%',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#D97706', fontWeight: 700, fontSize: 13 }}>
                <SafetyCertificateOutlined /> 3. Zero Over-delivery
              </div>
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
                GR items checked against outstanding PO quantity. Excess quantity strictly rejected.
              </div>
            </div>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: '#F8FAFC',
                border: '1px solid #E2E8F0',
                height: '100%',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#16A34A', fontWeight: 700, fontSize: 13 }}>
                <DatabaseOutlined /> 4. Atomic Cost Layers
              </div>
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
                Good received qty creates stock & FIFO cost layers. Damaged qty stored for vendor audit.
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Main Reports Tabs */}
      <Card
        variant="borderless"
        style={{
          borderRadius: 14,
          background: '#FFFFFF',
          boxShadow: PASTEL_THEME.boxShadowCard,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <Title level={4} style={{ margin: 0, color: '#0F172A', fontWeight: 700 }}>
            Procurement Reports & Traceability Views
          </Title>
          <Input
            placeholder="Search report items..."
            prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: 280, borderRadius: 10 }}
            allowClear
          />
        </div>

        <Tabs
          activeKey={activeReportTab}
          onChange={setActiveReportTab}
          items={[
            {
              key: 'outstanding',
              label: (
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  <ShoppingOutlined style={{ marginRight: 6 }} />
                  Outstanding PO Report ({filteredOutstanding.length})
                </span>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Real-time fulfillment progress for active supplier commitments.
                    </Text>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => router.push('/reports')}
                      style={{ fontWeight: 600 }}
                    >
                      Open Full Reports Page &rarr;
                    </Button>
                  </div>
                  <Table
                    columns={outstandingColumns}
                    dataSource={filteredOutstanding}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 5 }}
                    bordered={false}
                  />
                </div>
              ),
            },
            {
              key: 'traceability',
              label: (
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  <BranchesOutlined style={{ marginRight: 6 }} />
                  Traceability Ledger ({filteredTraceability.length})
                </span>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Downstream audit trail: PR &rarr; PO &rarr; GR &rarr; Inventory Cost Layers.
                    </Text>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => router.push('/reports')}
                      style={{ fontWeight: 600 }}
                    >
                      Open Full Reports Page &rarr;
                    </Button>
                  </div>
                  <Table
                    columns={traceabilityColumns}
                    dataSource={filteredTraceability}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 5 }}
                    bordered={false}
                  />
                </div>
              ),
            },
            {
              key: 'quality',
              label: (
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  <SafetyCertificateOutlined style={{ marginRight: 6 }} />
                  Supplier Performance & Quality ({filteredQuality.length})
                </span>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Vendor delivery inspection metrics: good items accepted vs defects rejected.
                    </Text>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => router.push('/reports')}
                      style={{ fontWeight: 600 }}
                    >
                      Open Full Reports Page &rarr;
                    </Button>
                  </div>
                  <Table
                    columns={qualityColumns}
                    dataSource={filteredQuality}
                    rowKey="supplierId"
                    loading={loading}
                    pagination={{ pageSize: 5 }}
                    bordered={false}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
